import React from 'react';
import { Button, Input, Select } from '../../ui';
import { FolderType, SourceFilter } from './helpers';
import { LibraryViewMode } from './useAttachmentLibraryState';
import { Density, SortKey, SORT_OPTIONS } from './mediaCardUtils';
import { MediaFilterPreset } from './mediaLibraryQuery';

interface MediaFilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchEnter: () => void;
  isSearching: boolean;

  viewMode: LibraryViewMode;
  onViewModeChange: (mode: LibraryViewMode) => void;

  folderType: FolderType;
  onFolderTypeChange: (folder: FolderType) => void;
  folderCounts: Record<FolderType, number>;

  sourceFilter: SourceFilter;
  onSourceFilterChange: (source: SourceFilter) => void;

  density: Density;
  onDensityChange: (density: Density) => void;

  sortKey: SortKey;
  onSortChange: (key: SortKey) => void;

  hasControlnet?: boolean;
  onHasControlnetChange?: (enabled: boolean) => void;
  recentDays?: number | null;
  onRecentDaysChange?: (days: number | null) => void;
  presets?: MediaFilterPreset[];
  activePresetId?: string | null;
  onPresetApply?: (presetId: string) => void;
  onPresetSave?: (name: string) => void;
  onPresetDelete?: (presetId: string) => void;
}

interface ChipProps {
  active: boolean;
  count?: number;
  onClick: () => void;
  children: React.ReactNode;
  label: string;
}

const Chip: React.FC<ChipProps> = ({ active, count, onClick, children, label }) => (
  <button
    type="button"
    className="media-chip"
    data-active={active}
    aria-pressed={active}
    aria-label={label}
    onClick={onClick}
  >
    {children}
    {typeof count === 'number' && <span className="media-chip-count">· {count}</span>}
  </button>
);

const DENSITY_STOPS: Array<{ value: Density; glyph: string; label: string }> = [
  { value: 'comfortable', glyph: '▭', label: 'Comfortable' },
  { value: 'cozy', glyph: '▮', label: 'Cozy' },
  { value: 'compact', glyph: '▪', label: 'Compact' },
];

/**
 * One consolidated, sticky filter bar — search + facet chips + density + sort —
 * replacing the divergent folder grid, view-pill row, and lonely source select.
 * Chips reuse the neo-brutalist pill idiom (indigo when active, ghost when not);
 * sort reuses the custom Select dropdown; density is a 3-stop segmented control.
 */
const MediaFilterBar: React.FC<MediaFilterBarProps> = ({
  searchValue,
  onSearchChange,
  onSearchEnter,
  isSearching,
  viewMode,
  onViewModeChange,
  folderType,
  onFolderTypeChange,
  folderCounts,
  sourceFilter,
  onSourceFilterChange,
  density,
  onDensityChange,
  sortKey,
  onSortChange,
  hasControlnet = false,
  onHasControlnetChange = () => {},
  recentDays = null,
  onRecentDaysChange = () => {},
  presets = [],
  activePresetId = null,
  onPresetApply = () => {},
  onPresetSave = () => {},
  onPresetDelete = () => {},
}) => {
  const [showPresetName, setShowPresetName] = React.useState(false);
  const [presetName, setPresetName] = React.useState('');
  const activePreset = presets.find((preset) => preset.id === activePresetId);

  const savePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    onPresetSave(name);
    setPresetName('');
    setShowPresetName(false);
  };

  return (
    <div className="media-filter-bar">
      {/* Row 1 — search + density + sort */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[180px]">
          <Input
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearchEnter();
            }}
            placeholder="Search prompts, names…"
            aria-label="Search media"
          />
        </div>

        <div
          className="media-density-seg"
          role="group"
          aria-label="Grid density"
          title="Grid density"
        >
          {DENSITY_STOPS.map((stop) => (
            <button
              key={stop.value}
              type="button"
              className="media-density-btn"
              data-active={density === stop.value}
              aria-pressed={density === stop.value}
              aria-label={stop.label}
              title={stop.label}
              onClick={() => onDensityChange(stop.value)}
            >
              {stop.glyph}
            </button>
          ))}
        </div>

        <Select
          options={SORT_OPTIONS}
          value={sortKey}
          selectSize="sm"
          onChange={(e) => onSortChange(e.target.value as SortKey)}
          className="min-w-[120px] flex-1 sm:w-[150px] sm:flex-none"
          aria-label="Sort media"
        />

        <select
          className="select select-sm min-w-[170px] flex-1 sm:min-w-[190px] sm:flex-none"
          aria-label="Filter preset"
          value={activePresetId || ''}
          onChange={(event) => {
            if (event.target.value) onPresetApply(event.target.value);
          }}
        >
          <option value="">Filter presets…</option>
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.builtIn ? 'Built-in · ' : ''}{preset.name}
            </option>
          ))}
        </select>

        <Button type="button" variant="ghost" size="sm" onClick={() => setShowPresetName(true)}>
          Save preset
        </Button>
        {activePreset && !activePreset.builtIn && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onPresetDelete(activePreset.id)}
          >
            Delete preset
          </Button>
        )}
      </div>

      {showPresetName && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[220px] flex-1">
            <Input
              value={presetName}
              onChange={(event) => setPresetName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') savePreset();
                if (event.key === 'Escape') setShowPresetName(false);
              }}
              placeholder="Preset name"
              aria-label="Preset name"
              autoFocus
            />
          </div>
          <Button type="button" variant="primary" size="sm" onClick={savePreset} disabled={!presetName.trim()}>
            Save
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowPresetName(false)}>
            Cancel
          </Button>
        </div>
      )}

      {isSearching && (
        <span className="attachment-meta" style={{ color: 'var(--accent-strong)' }}>
          Searching name + prompt
        </span>
      )}

      {/* Row 2 — facet chips (smart views + types + source) */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip active={viewMode === 'all'} label="All media" onClick={() => onViewModeChange('all')}>
          All
        </Chip>
        <Chip
          active={viewMode === 'favourites'}
          label="Favourites"
          onClick={() => onViewModeChange('favourites')}
        >
          ★ Favourites
        </Chip>
        <Chip
          active={viewMode === 'uploads'}
          label="Uploads"
          onClick={() => onViewModeChange('uploads')}
        >
          Uploads
        </Chip>
        <Chip
          active={viewMode === 'grouped'}
          label="Grouped by run"
          onClick={() => onViewModeChange('grouped')}
        >
          Grouped
        </Chip>

        <span className="mx-1 text-[var(--border-strong)]" aria-hidden="true">
          ·
        </span>

        <Chip
          active={folderType === 'image'}
          count={folderCounts.image}
          label="Images"
          onClick={() => onFolderTypeChange(folderType === 'image' ? 'all' : 'image')}
        >
          Images
        </Chip>
        <Chip
          active={folderType === 'video'}
          count={folderCounts.video}
          label="Video"
          onClick={() => onFolderTypeChange(folderType === 'video' ? 'all' : 'video')}
        >
          Video
        </Chip>
        <Chip
          active={folderType === 'audio'}
          count={folderCounts.audio}
          label="Audio"
          onClick={() => onFolderTypeChange(folderType === 'audio' ? 'all' : 'audio')}
        >
          Audio
        </Chip>

        <span className="mx-1 text-[var(--border-strong)]" aria-hidden="true">
          ·
        </span>

        <Chip
          active={sourceFilter === 'generated'}
          label="Generated source"
          onClick={() => onSourceFilterChange(sourceFilter === 'generated' ? 'all' : 'generated')}
        >
          Generated
        </Chip>

        <Chip
          active={hasControlnet}
          label="ControlNet recipe"
          onClick={() => onHasControlnetChange(!hasControlnet)}
        >
          ControlNet
        </Chip>

        {recentDays && (
          <Chip
            active
            label={`Created in last ${recentDays} days`}
            onClick={() => onRecentDaysChange(null)}
          >
            Last {recentDays} days ×
          </Chip>
        )}
      </div>
    </div>
  );
};

export default MediaFilterBar;
